/* ============================================================================
 * TrackingLinkModal — shared modal that creates a tracking_sessions
 * row, then renders the resulting /track/:token link with copy /
 * WhatsApp / SMS shortcuts.
 *
 * Used from:
 *   - ShipmentRegistry kebab menu (admin side)
 *   - ShipperPortal proforma toolbar (shipper side)
 *
 * Both callers pass the same `payload` shape so the row written to
 * Supabase looks identical regardless of who clicked. The token is
 * generated client-side (crypto.randomUUID with a Date.now fallback
 * for older browsers).
 *
 * The phone number is taken verbatim from shipment.transport.phone
 * which is stored as "+DIAL-AREA-000-0000" (see parsePhone in
 * ShipmentRegistry). We strip everything but digits when building
 * wa.me / sms: URIs, since those scheme handlers expect raw digits.
 * ========================================================================= */

import { useEffect, useState } from "react";
import DraggableModal from "./DraggableModal";
import { supabase } from "../supabaseClient";

/* Production driver-page origin. The driver opens this from his
   phone outside the admin SPA, so it must be the public domain. If
   the operator is testing locally we still build the prod link —
   the SPA at localhost can't receive WhatsApp clicks anyway. */
var DRIVER_LINK_BASE = "https://nexus.ground-fresh.com/track/";

var T = {
  en: {
    title: "Send Tracking Link",
    creating: "Creating link…",
    error: "Could not create tracking link.",
    intro: "Share this link with the driver. The link will be active until they reach the border or you stop the session.",
    link: "Tracking link",
    copy: "Copy link", copied: "Copied",
    whatsapp: "Send via WhatsApp", sms: "Send via SMS",
    phoneMissing: "No driver phone on this shipment — fill it in to send WhatsApp / SMS.",
    msgTemplate: "GFI Tracking — open this link to share your location: ",
    close: "Close",
  },
  es: {
    title: "Enviar Link de Rastreo",
    creating: "Creando link…",
    error: "No se pudo crear el link de rastreo.",
    intro: "Comparte este link con el chofer. El link estará activo hasta que llegue a la frontera o detengas la sesión.",
    link: "Link de rastreo",
    copy: "Copiar link", copied: "Copiado",
    whatsapp: "Enviar por WhatsApp", sms: "Enviar por SMS",
    phoneMissing: "No hay teléfono del chofer en este embarque — captúralo para enviar WhatsApp / SMS.",
    msgTemplate: "GFI Tracking — abre este link para compartir tu ubicación: ",
    close: "Cerrar",
  },
};

function newToken() {
  try { if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID(); }
  catch (e) { /* fall through */ }
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

/* Strip everything but digits — sms: and wa.me both want raw digits.
   The stored phone keeps "+52-555-123-4567" formatting; the leading
   plus is dropped here since wa.me doesn't accept it. */
function digitsOnly(phone) {
  return String(phone || "").replace(/\D/g, "");
}

/* Pull the driver phone off a shipment / proforma payload. Either
   transport.phone (admin form) or transport.phone (shipper form) —
   both use the same key. */
function pickPhone(payload) {
  if (!payload) return "";
  if (payload.driver_phone) return payload.driver_phone;
  var trp = payload.transport || {};
  return trp.phone || "";
}

/* Pull driver name with the same fallback chain. */
function pickDriver(payload) {
  if (!payload) return "";
  if (payload.driver_name) return payload.driver_name;
  var trp = payload.transport || {};
  return trp.driver || "";
}

/* Pull the LOT number — admin shipments expose it as `lot`, portal
   proformas don't have one until acceptance so we fall back to
   `consec` / `proformaNumber`. */
function pickLot(payload) {
  if (!payload) return "";
  return payload.lot || payload.consec || payload.proformaNumber || "";
}

function pickShipperName(payload) {
  if (!payload) return "";
  return payload.shipperName || payload.shipper_name || "";
}

export default function TrackingLinkModal({ lang, payload, onClose }) {
  var t = T[lang] || T.es;
  var [phase, setPhase] = useState("creating"); // creating | ready | error
  var [link, setLink] = useState("");
  var [copied, setCopied] = useState(false);
  var [errMsg, setErrMsg] = useState("");

  /* Create the row once on mount. We deliberately do NOT key this on
     payload.id — if the operator opens the modal twice, they get two
     tokens, which matches the spec ("the most recent link wins"). */
  useEffect(function () {
    var alive = true;
    var token = newToken();
    var row = {
      shipment_id: payload && payload.id ? String(payload.id) : null,
      token: token,
      driver_name: pickDriver(payload),
      driver_phone: pickPhone(payload),
      lot: String(pickLot(payload) || ""),
      shipper_name: pickShipperName(payload),
      status: "pending",
    };
    supabase.from("tracking_sessions").insert([row]).select().single()
      .then(function (res) {
        if (!alive) return;
        if (res.error || !res.data) { setPhase("error"); setErrMsg(res.error ? res.error.message : "no-row"); return; }
        setLink(DRIVER_LINK_BASE + token);
        setPhase("ready");
      })
      .catch(function (e) { if (alive) { setPhase("error"); setErrMsg(String(e && e.message ? e.message : e)); } });
    return function () { alive = false; };
  }, [payload]);

  var phone = pickPhone(payload);
  var hasPhone = !!digitsOnly(phone);
  var msg = t.msgTemplate + link;
  var waHref = hasPhone ? ("https://wa.me/" + digitsOnly(phone) + "?text=" + encodeURIComponent(msg)) : null;
  var smsHref = hasPhone ? ("sms:+" + digitsOnly(phone) + "?body=" + encodeURIComponent(msg)) : null;

  var copyLink = function () {
    if (!link) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(link).then(function () { setCopied(true); setTimeout(function () { setCopied(false); }, 1800); });
      } else {
        /* Fallback for older browsers — temporary textarea + execCommand. */
        var ta = document.createElement("textarea");
        ta.value = link; document.body.appendChild(ta); ta.select();
        try { document.execCommand("copy"); setCopied(true); setTimeout(function () { setCopied(false); }, 1800); }
        finally { document.body.removeChild(ta); }
      }
    } catch (e) { /* swallow */ }
  };

  var btn = {
    padding: "10px 14px",
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "transparent",
    color: "var(--color-text)",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    justifyContent: "center",
  };
  var btnAccent = Object.assign({}, btn, { background: "var(--color-accent)", color: "var(--color-accent-text)", border: "none" });
  var btnGreen  = Object.assign({}, btn, { background: "#25D366", color: "#fff", border: "none" });
  var btnBlue   = Object.assign({}, btn, { background: "#1B3A4B", color: "#fff", border: "none" });

  return (
    <DraggableModal title={t.title} onClose={onClose}>
      <div style={{ padding: "20px 24px 24px", maxWidth: 560 }}>
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 16, lineHeight: 1.5 }}>{t.intro}</div>

        {phase === "creating" && (
          <div style={{ padding: "16px 12px", fontSize: 13, color: "var(--color-text-muted)" }}>{t.creating}</div>
        )}
        {phase === "error" && (
          <div style={{ padding: "12px 14px", borderRadius: 8, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 13, fontWeight: 600 }}>
            {t.error}
            {errMsg && <div style={{ fontSize: 11, fontWeight: 400, marginTop: 4, color: "#B91C1C" }}>{errMsg}</div>}
          </div>
        )}

        {phase === "ready" && (
          <>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{t.link}</div>
              <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
                <input
                  readOnly
                  value={link}
                  onClick={function (e) { e.target.select(); }}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 6,
                    border: "1px solid var(--color-border)",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 12,
                    background: "var(--color-bg)",
                    color: "var(--color-text)",
                  }}
                />
                <button onClick={copyLink} style={btnAccent}>
                  {copied ? "✓ " + t.copied : t.copy}
                </button>
              </div>
            </div>

            {!hasPhone && (
              <div style={{ marginBottom: 14, padding: "10px 12px", borderRadius: 6, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.25)", color: "#92400E", fontSize: 12, fontWeight: 600 }}>{t.phoneMissing}</div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: hasPhone ? "1fr 1fr" : "1fr", gap: 8 }}>
              {hasPhone && (
                <a href={waHref} target="_blank" rel="noreferrer" style={btnGreen}>
                  <span style={{ fontSize: 16 }}>💬</span> {t.whatsapp}
                </a>
              )}
              {hasPhone && (
                <a href={smsHref} style={btnBlue}>
                  <span style={{ fontSize: 16 }}>📱</span> {t.sms}
                </a>
              )}
            </div>
          </>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 22 }}>
          <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.close}</button>
        </div>
      </div>
    </DraggableModal>
  );
}
